import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setShop } from '../../redux/features/shopSlice';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import TextField from '@mui/material/TextField';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import useCaseTicket from './useCaseTicket';
import CaseTicketService from './CaseTicketService';
import CaseTicketStatusStepper from './CaseTicketStatusStepper';
import RefundCalculator from './RefundCalculator';
import DiagnosisChip from './DiagnosisChip';
import OrderPicker from './OrderPicker';
import WarehousePanel from './WarehousePanel';
import ShopBadge from './ShopBadge';
import SupervisorAssistPanel from './SupervisorAssistPanel';
import SupervisorLapsePanel from './LapsePanel';
import { WeightAssistTag } from './WeightAssistTag';
import ImageModal from '../weeklyShip/ImageModel';
import {
  ISSUE_TYPE_LABELS, SHIPPING_CHANNEL_LABELS, ISSUE_TYPE_TO_REFUND_REASON, STATUS_LABELS, FOUND_STATUS_LABELS, ticketDisplayName,
} from './constants';

const formatLogTime = (iso) =>
  new Date(iso).toLocaleString('th-TH', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
// Baht, always 2 decimals — matches the figure in the customer's message.
const baht = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ตามของ after ✓ แจ้งลูกค้าแล้ว: nobody owes anything until the sweep hands it back.
const HUNTING_NOTE = '🔎 แจ้งลูกค้าแล้ว — กำลังตามหา ระบบเช็คทุกชั่วโมง ถ้าเจอของหรือครบกำหนดจะร่างข้อความใหม่ให้';

// ── The 📜 log in plain words: who did what, and whose move it was next ──────
// statusHistory stores only the status entered; the action is read from where
// the case came from (e.g. waiting_supervisor after waiting_warehouse = Kluay's answer).
const historyActor = (h) => (h.changedBy == null ? '🔔 ระบบ'
  : h.changedByName === 'Kluay' ? '👤 Kluay'
    : `👤 ${h.changedByName || `user #${h.changedBy}`} (${h.changedByRole === 'superuser' ? 'หัวหน้า' : 'แอดมิน'})`);
const historyAction = (h, from) => {
  if (from === 'closed' && h.status !== 'closed') return 'เปิดเคสกลับ';
  switch (h.status) {
    case 'open': return 'เปิดเคส';
    case 'waiting_warehouse': return 'ส่งให้ Kluay เช็ค';
    case 'waiting_supervisor':
      if (h.changedBy == null) return 'พบของแต่ตกหล่น — ส่งให้หัวหน้าเลือกชดเชย';
      return from === 'waiting_warehouse' ? 'บันทึกผลจาก Kluay'
        : ['need_info', 'waiting_customer'].includes(from) ? 'ส่งคำตอบให้หัวหน้า'
          : from === 'customer_informed' ? 'ส่งกลับหัวหน้า (ลูกค้าไม่ตกลง)' : 'ส่งให้หัวหน้า';
    case 'need_info': return from === 'in_progress' ? 'ให้แอดมินทำ/ตอบกลับ' : 'ขอข้อมูลเพิ่ม';
    case 'waiting_customer': return 'ถามลูกค้าแล้ว รอตอบ';
    case 'in_progress': return 'รับเรื่อง';
    case 'customer_update': return h.changedBy == null ? 'ร่างข้อความถึงลูกค้า (เจอของ / ครบกำหนด)' : 'ตัดสินใจ — ร่างข้อความถึงลูกค้า';
    case 'customer_informed': return '✓ แจ้งลูกค้าแล้ว';
    case 'customer_confirmed': return 'ลูกค้าตกลง';
    case 'closed': return 'ปิดเคส';
    case 'extend_followup': return 'ต่อเวลาติดตาม +2 วัน';
    default: return STATUS_LABELS[h.status] || h.status;
  }
};
const historyNext = (status, isTracking) => ({
  waiting_warehouse: 'Kluay เช็คของ',
  waiting_supervisor: 'หัวหน้าตัดสินใจ',
  need_info: 'แอดมินหาข้อมูล / ตอบ',
  waiting_customer: 'ลูกค้าตอบ',
  in_progress: 'แอดมินแจ้งลูกค้า → หัวหน้าติดตามผล',
  customer_update: 'แอดมินแจ้งลูกค้า',
  customer_informed: isTracking ? 'ระบบตามหาของ' : 'ลูกค้าตอบ',
  customer_confirmed: 'แอดมินโอนเงินคืน',
  closed: 'จบเคส',
}[status] || null);

const CaseTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const isSupervisor = authState?.user?.role === 'superuser';

  const {
    caseTicket, comments, fetchCaseTicket, fetchComments, addComment, waitCustomer,
    customerUpdateCaseTicket, confirmCustomer, closeCaseTicket, reopenCaseTicket, uploadAttachments,
  } = useCaseTicket();

  const [newComment, setNewComment] = useState('');
  // The supervisor's card: the decision form (message + amount) is opened on demand.
  const [showDecision, setShowDecision] = useState(false);
  const [taskReply, setTaskReply] = useState('');
  const [customerUpdateMessage, setCustomerUpdateMessage] = useState('');
  const [proposedRefundAmount, setProposedRefundAmount] = useState('');
  const [reopenStatus, setReopenStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);
  // ตามของ: one more chase, one more parcel
  const [chaseNote, setChaseNote] = useState('');

  // Claim-on-open bookkeeping: holdRef = we claimed it, actedRef = we did a real
  // action. Leaving the page without acting releases silently (read ≠ own).
  const holdRef = useRef(false);
  const actedRef = useRef(false);

  const load = () => { fetchCaseTicket(id); fetchComments(id); };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // A case from the other shop (e.g. one I took from "my cases") switches the
  // sidebar shop to it, so whatever I do next is in the right shop. Kluay and
  // superusers work both shops and keep the current selection.
  const dispatch = useDispatch();
  const shopName = useSelector((state) => state.shop.name);
  const caseShop = caseTicket?.id === Number(id) ? caseTicket.customer?.shop : null;
  useEffect(() => {
    if (!caseShop || caseShop === shopName || isSupervisor || authState?.user?.nickname === 'Kluay') return;
    dispatch(setShop(caseShop));
    toast.info(`เคสนี้เป็นของร้าน ${caseShop} — สลับร้านเป็น ${caseShop} แล้ว`);
  }, [caseShop]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}/admin-dashboard`, { withCredentials: true });
    socket.on('case_ticket_updated', (payload) => { if (String(payload?.id) === String(id)) load(); });
    socket.on('case_claim_updated', (payload) => {
      if (payload?.caseType === 'case_ticket' && String(payload?.caseKey) === String(id)) load();
    });
    return () => socket.disconnect();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Claim only on DELIBERATE signals — never passively. An idle open tab must
  // not own a case (its admin may have left her shift): when the ball passes to
  // the admins, the case stays unclaimed until someone available opens it fresh
  // or acts on it. So:
  //  - claim-on-open fires ONCE per navigation, only if the case is already in
  //    an admin-work stage at load time (supervisor opens never claim)
  //  - ensureClaimed() below covers "tab was already open when the stage
  //    flipped, and the admin then chose to act"
  useEffect(() => {
    if (!caseTicket || isSupervisor || holdRef.current) return;
    // customer_informed: the customer's answer comes back to an admin — except on
    // ตามของ, where nothing is owed until the sweep hands the case back.
    const claimable = ['need_info', 'in_progress', 'customer_update', 'customer_confirmed'].includes(caseTicket.status)
      || (caseTicket.status === 'customer_informed' && caseTicket.issueType !== 'tracking_follow');
    if (!claimable) return;
    if (caseTicket.claimedByUserId && !caseTicket.claimedByMe) return; // held — don't auto-steal
    CaseTicketService.claimCaseTicket(id, false)
      .then(() => { holdRef.current = true; load(); })
      .catch((err) => {
        if (err.response?.status === 409) load(); // lost the race — show the holder chip
      });
  }, [caseTicket?.id, isSupervisor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Acting on an unclaimed case claims it as a side effect (claim-then-act, like
  // the cancel tab). Fire-and-forget: a 400 (e.g. commenting extra info while
  // still waiting on the supervisor) just means there's nothing to claim.
  const ensureClaimed = () => {
    if (isSupervisor || holdRef.current || claimedByOther) return;
    CaseTicketService.claimCaseTicket(id, false)
      .then(() => { holdRef.current = true; })
      .catch(() => {});
  };

  const handleTakeover = async () => {
    if (!window.confirm(`เคสนี้กำลังดำเนินการโดย ${caseTicket.claimedByName || 'แอดมินท่านอื่น'}\nยืนยันจะรับช่วงต่อหรือไม่?`)) return;
    try {
      await CaseTicketService.claimCaseTicket(id, true);
      holdRef.current = true;
      actedRef.current = true;
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'รับเคสไม่สำเร็จ');
    }
  };

  // Auto-release when leaving without having acted — nobody has to remember.
  useEffect(() => {
    return () => {
      if (holdRef.current && !actedRef.current) {
        CaseTicketService.releaseCaseTicket(id).catch(() => {});
      }
    };
  }, [id]);

  useEffect(() => {
    if (!caseTicket) return;
    setProposedRefundAmount(caseTicket.proposedRefundAmount ?? '');
  }, [caseTicket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!caseTicket) return <div className="p-6 text-gray-400">กำลังโหลด...</div>;

  const isInternal = caseTicket.type === 'internal';
  const isTracking = caseTicket.issueType === 'tracking_follow';
  const claimedByOther = caseTicket.claimedByUserId && !caseTicket.claimedByMe;
  const items = Array.isArray(caseTicket.items) ? caseTicket.items : [];
  // App (wallet) customer: a lost parcel is refunded into their wallet by a person
  // on the refund page — the ตามของ click only closes the case and the orders.
  const isAppCustomer = !!caseTicket.customer?.userId;
  // Found parcels the customer has not heard about yet — the case stays open
  // until ✓ แจ้งลูกค้าแล้ว, and closes then unless something is still being hunted.
  const untoldFound = items.filter((it) => it.foundStatus === 'found' && !it.foundToldAt);
  const stillHunting = items.some((it) => it.foundStatus === 'pending');

  // ตามของ: the customer chased again — log it, the LINE group hears the count.
  const handleAddChase = async () => {
    setSaving(true);
    try {
      const res = await CaseTicketService.addChase(id, chaseNote.trim() || undefined);
      actedRef.current = true;
      setChaseNote('');
      toast.success(`บันทึกแล้ว — ลูกค้าทวงครั้งที่ ${res.data.chaseCount}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async (order) => {
    setSaving(true);
    try {
      await CaseTicketService.addItem(id, { orderId: order.id });
      actedRef.current = true;
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'เพิ่มรายการไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveItem = async (item) => {
    if (!window.confirm(`ลบรายการนี้ออกจากเคส?\n${item.url || ''}`)) return;
    try {
      await CaseTicketService.removeItem(id, item.id);
      actedRef.current = true;
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'ลบไม่สำเร็จ');
    }
  };

  const handleAddComment = async (action, textArg) => {
    const text = (textArg ?? newComment).trim();
    if (!text) { toast.error('กรุณากรอกข้อความ'); return; }
    ensureClaimed();
    const handsBallBack = action === 'answer' || action === 'counter';
    setSaving(true);
    const created = await addComment(id, text, action);
    setSaving(false);
    // The server also broadcasts 'case_ticket_updated' (e.g. on รับเรื่องแล้ว
    // moving the status), which triggers a full reload — no need to duplicate.
    if (created) {
      actedRef.current = true;
      setNewComment('');
      setTaskReply('');
      // Posting the answer hands the case to the supervisor — the backend
      // releases the claim; the admin's session on this case is done.
      if (handsBallBack) {
        holdRef.current = false;
        toast.success('ส่งคำตอบให้หัวหน้าแล้ว — จบงานเคสนี้');
        navigate('/case-ticket');
      }
    }
  };

  // The accept-path hand-back: supervisor pressed รับเรื่องแล้ว → admin must
  // tell the customer "we're working on it" and record that with one click.
  // Posts a fixed comment (admin becomes latest author → off the banner, case
  // parks with the supervisor + 2-day digest), releases the claim, session done.
  // Internal tickets use the same mechanics with รับทราบ wording.
  const handleInformedCustomer = async (confirmText, msgText) => {
    if (!window.confirm(confirmText || 'ยืนยันว่าแจ้งลูกค้าแล้วว่ากำลังดำเนินการอยู่?')) return;
    setSaving(true);
    const created = await addComment(id, msgText || '📢 แจ้งลูกค้าแล้วว่ากำลังดำเนินการอยู่', 'informed');
    setSaving(false);
    if (created) {
      await CaseTicketService.releaseCaseTicket(id).catch(() => {});
      holdRef.current = false;
      actedRef.current = true;
      toast.success('บันทึกแล้ว — เคสรอหัวหน้าอัพเดทผล');
      navigate('/case-ticket');
    }
  };

  // ตามของ: the admin sent the drafted message by hand. On a case with past-due
  // parcels this same click files their refunds (or cancels the orders nothing
  // was paid on) for the baht in that message. If the money moved since, the
  // server refuses and posts a fresh draft — reload so the card shows it.
  const handleInformedTracking = async () => {
    const owed = items.filter((it) => it.foundStatus === 'not_found');
    const confirmText = !owed.length && untoldFound.length
      ? `ยืนยันว่าแจ้งลูกค้าแล้วว่าเจอของ?${stillHunting ? '' : '\nระบบจะปิดเคสให้'}`
      : !owed.length ? 'ยืนยันว่าแจ้งลูกค้าแล้ว?'
      : isAppCustomer
        ? 'ยืนยันว่าส่งข้อความให้ลูกค้าแล้ว?\nระบบจะปิดเคส ยกเลิกออเดอร์ และคืนเงินเข้ากระเป๋าลูกค้าทันที ตามยอดในข้อความ'
        : 'ยืนยันว่าส่งข้อความให้ลูกค้าแล้ว?\nระบบจะสร้างรายการคืนเงิน (หรือยกเลิกออเดอร์ที่ยังไม่ได้ชำระ) ตามยอดในข้อความ';
    if (!window.confirm(confirmText)) return;
    setSaving(true);
    const created = await addComment(id, '📢 แจ้งลูกค้าแล้ว', 'informed');
    setSaving(false);
    if (!created) { load(); return; }
    await CaseTicketService.releaseCaseTicket(id).catch(() => {});
    holdRef.current = false;
    actedRef.current = true;
    // A slow payer's past-due parcel gives back ฿0 — its order is only cancelled.
    const refunded = owed.some((it) => Number(it.refundAmount) > 0);
    toast.success(refunded && isAppCustomer ? 'บันทึกแล้ว — ปิดเคสแล้ว คืนเงินเข้ากระเป๋าลูกค้าแล้ว'
      : refunded ? 'บันทึกแล้ว — สร้างรายการคืนเงินแล้ว ไปเก็บเลขบัญชีลูกค้าที่หน้าโอนเงินคืน'
        : owed.length ? 'บันทึกแล้ว — ยกเลิกออเดอร์แล้ว (ไม่มียอดต้องคืน)'
          : untoldFound.length && !stillHunting ? 'บันทึกแล้ว — ปิดเคสแล้ว' : 'บันทึกแล้ว');
    navigate('/case-ticket');
  };

  // Supervisor snooze: pushes the follow-up mark +2 days so the hourly digest
  // stops chasing (e.g. seller still hasn't replied). Logged in the 📜 history.
  const handleExtendFollowUp = async () => {
    if (!window.confirm('เลื่อนกำหนดติดตามออกไปอีก 2 วัน?')) return;
    try {
      await CaseTicketService.extendFollowUp(id);
      toast.success('เลื่อนติดตามไปอีก 2 วันแล้ว');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'เลื่อนไม่สำเร็จ');
    }
  };

  const handleWaitCustomer = async () => {
    // Deliberately NOT ensureClaimed — parking passes the ball to the customer,
    // so the case belongs to nobody while it waits (the asking admin may be off
    // shift when the answer arrives). The backend releases any claim too.
    const ok = await waitCustomer(id);
    if (ok) {
      holdRef.current = false; // backend just released our claim
      actedRef.current = true;
      toast.success('พักเคสไว้ รอลูกค้าตอบ — เคสจะกลับมาเมื่อมีข้อความใหม่');
    }
  };

  const handleReleaseClaim = async () => {
    if (!window.confirm('คืนเคสนี้ให้แอดมินท่านอื่นรับต่อ?')) return;
    await CaseTicketService.releaseCaseTicket(id).catch(() => {});
    holdRef.current = false;
    actedRef.current = true; // explicit release IS the action; don't double-release on unmount
    load();
  };

  const handleCustomerUpdate = async () => {
    if (!customerUpdateMessage.trim()) { toast.error('กรุณากรอกข้อความที่แจ้งลูกค้า'); return; }
    ensureClaimed();
    setSaving(true);
    const updated = await customerUpdateCaseTicket(id, {
      message: customerUpdateMessage.trim(),
      proposedRefundAmount: proposedRefundAmount === '' ? null : parseFloat(proposedRefundAmount),
    });
    setSaving(false);
    if (updated) {
      actedRef.current = true;
      toast.success('บันทึกการแจ้งลูกค้าแล้ว');
      setCustomerUpdateMessage('');
      setShowDecision(false);
      fetchComments(id); // the update lands in the thread as a kinded comment
    }
  };

  const handleGoToRefund = (ticket = caseTicket) => {
    // The case's own customer link is the source of truth — the order's
    // customerId is only a fallback (a case doesn't need a linked order).
    // The refund modal attaches orders by zipping url+id, so only pass the
    // order when it has a usable URL.
    actedRef.current = true;
    const orderUrl = ticket.order?.url && ticket.order.url !== '-' ? ticket.order.url : null;
    navigate('/refund', {
      state: {
        openCaseTicketRefund: true,
        caseTicketId: ticket.id,
        prefill: {
          customerId: ticket.customerId || ticket.order?.customerId || null,
          contact: ticket.customer?.contact || '',
          smileName: ticket.customer?.smileName || '',
          orderUrls: orderUrl ? [orderUrl] : [],
          orderIds: orderUrl && ticket.orderId ? [ticket.orderId] : [],
          reason: ISSUE_TYPE_TO_REFUND_REASON[ticket.issueType] || 'other',
          amount: ticket.proposedRefundAmount || '',
        },
      },
    });
  };

  // App (wallet) customer on a case closed BEFORE ✓ แจ้งลูกค้าแล้ว credited wallets: its
  // orders were closed with no money moved. Open the refund page's create modal on those
  // parcels so an admin credits the wallet there. No caseTicketId: the case is already
  // closed, and a wallet credit has no refund row to link back.
  const goToWalletRefund = (parcels) => {
    const paid = parcels.filter((it) => Number(it.refundAmount) > 0 && it.orderId && (it.order?.url || it.url));
    if (!paid.length) return;
    navigate('/refund', {
      state: {
        openCaseTicketRefund: true,
        prefill: {
          customerId: caseTicket.customerId,
          contact: caseTicket.customer?.contact || '',
          smileName: caseTicket.customer?.smileName || '',
          orderUrls: paid.map((it) => it.order?.url || it.url),
          orderIds: paid.map((it) => it.orderId),
          reason: 'not_found',
          amount: Math.round(paid.reduce((sum, it) => sum + Number(it.refundAmount), 0) * 100) / 100,
        },
      },
    });
  };

  const handleConfirmCustomer = async () => {
    const hasMoney = parseFloat(caseTicket.proposedRefundAmount) > 0;
    const confirmText = hasMoney
      ? 'ยืนยันว่าลูกค้าตกลงยอด/แนวทางนี้แล้ว?\nระบบจะพาไปหน้าโอนเงินคืนทันที'
      : 'ยืนยันว่าลูกค้าตกลงตามนี้ (ไม่มีการคืนเงิน)?\nเคสจะปิดทันที';
    if (!window.confirm(confirmText)) return;
    ensureClaimed();
    const updated = await confirmCustomer(id);
    if (updated) {
      actedRef.current = true;
      if (updated.status === 'closed') {
        // Agreed no-refund resolution — the backend closed it mechanically.
        holdRef.current = false;
        toast.success('ลูกค้าตกลง — ปิดเคสเรียบร้อย');
        navigate('/case-ticket');
      } else {
        toast.success('บันทึกการยืนยันของลูกค้าแล้ว');
        // Confirm and refund are one motion — jump straight to the prefilled
        // modal so "confirmed but forgot the refund" can barely happen.
        handleGoToRefund(updated);
      }
    }
  };

  const handleClose = async () => {
    if (!window.confirm('ยืนยันปิดเคสนี้?')) return;
    const updated = await closeCaseTicket(id);
    if (updated) { actedRef.current = true; toast.success('ปิดเคสแล้ว'); }
  };

  const handleReopen = async () => {
    if (!reopenStatus) { toast.error('กรุณาเลือกสถานะที่จะย้อนกลับไป'); return; }
    if (!window.confirm('ยืนยันเปิดเคสนี้กลับ?')) return;
    setSaving(true);
    const updated = await reopenCaseTicket(id, reopenStatus);
    setSaving(false);
    if (updated) { actedRef.current = true; toast.success('เปิดเคสกลับแล้ว'); setReopenStatus(''); }
  };

  const handleExtraFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const ok = await uploadAttachments(id, files);
    if (ok) { actedRef.current = true; toast.success('แนบไฟล์เพิ่มแล้ว'); }
    e.target.value = '';
  };

  const attachments = Array.isArray(caseTicket.attachments) ? caseTicket.attachments : [];
  // ── The admin's ONE action card ──────────────────────────────────────────
  // Admins never type in the thread (it's read-only history for them) — every
  // action, including answering after a park, lives in this single card. The
  // admin never reasons about statuses: the header says what to do, the card
  // shows the latest instruction and the button that completes it.
  // The supervisor's latest word. Whether it still has to be sent is the
  // backend's turn (caseTicket.turn) — a chase or system note after it never hides it.
  const lastSupervisorMsg = [...comments].reverse().find((c) => c.kind === 'comment' && c.author?.role === 'superuser')?.message;
  // The newest drafted message — from the supervisor (the promise, the decision)
  // or the hourly sweep with no author (refund-due, found). customer_update =
  // not sent yet; customer_informed = the admin pressed ✓ แจ้งลูกค้าแล้ว.
  const latestDraft = [...comments].reverse().find((c) => c.kind === 'customer_update');
  const adminTask = (() => {
    if (isSupervisor || !caseTicket || caseTicket.status === 'closed') return null;
    // Someone else is on it — don't offer action inputs, point at takeover.
    const actionableHere = ['need_info', 'in_progress', 'customer_update', 'customer_confirmed'].includes(caseTicket.status)
      || (caseTicket.status === 'customer_informed' && !isTracking);
    if (claimedByOther && actionableHere) return { kind: 'held' };
    if (caseTicket.status === 'need_info') {
      return { kind: 'answer', title: '📌 หัวหน้ารอคำตอบจากคุณ', instruction: lastSupervisorMsg };
    }
    if (caseTicket.status === 'waiting_customer') {
      return { kind: 'answer', waiting: true, title: '⏳ รอลูกค้าตอบ', instruction: lastSupervisorMsg };
    }
    if (caseTicket.status === 'in_progress' && caseTicket.turn === 'admin') {
      return isInternal
        ? { kind: 'ack', title: '📌 หัวหน้าตอบกลับแล้ว', instruction: lastSupervisorMsg }
        : { kind: 'inform', title: '📌 แจ้งลูกค้าตามนี้', instruction: lastSupervisorMsg };
    }
    // One-way on ตามของ: there is no "customer agrees" step — relay, then wait.
    if (isTracking && caseTicket.status === 'customer_update') {
      return { kind: 'informTracking', title: '📌 แจ้งลูกค้าตามนี้', instruction: latestDraft?.message,
        from: latestDraft?.author ? '👤 หัวหน้า' : '🔔 ระบบ' };
    }
    if (isTracking && caseTicket.status === 'customer_informed') {
      return { kind: 'info', title: HUNTING_NOTE };
    }
    // Decision made → relay it first; after แจ้งลูกค้าแล้ว the card is the agree/counter step.
    if (!isInternal && caseTicket.status === 'customer_update') {
      return { kind: 'informDecision', title: '📌 แจ้งผลลูกค้าตามนี้', instruction: latestDraft?.message };
    }
    if (!isInternal && caseTicket.status === 'customer_informed') {
      return { kind: 'confirm', title: '📌 รอลูกค้าตกลง', instruction: latestDraft?.message };
    }
    if (!isInternal && caseTicket.status === 'customer_confirmed') {
      return { kind: 'refund', title: '📌 สร้างรายการโอนเงินคืน' };
    }
    if (caseTicket.status === 'waiting_warehouse') {
      return { kind: 'note', title: '📦 เคสอยู่กับ Kluay — รอผลเช็ค (ลูกค้าทวงอีก? กด 📣 ด้านล่าง)' };
    }
    if (caseTicket.status === 'waiting_supervisor') {
      // Nothing required — but keep ONE consistent place to add info if needed.
      return { kind: 'note', title: '💬 เคสอยู่กับหัวหน้า — ไม่มีอะไรต้องทำตอนนี้' };
    }
    return null;
  })();

  // ── The supervisor's ONE card ─────────────────────────────────────────────
  // Same idea as the admin's: what to do now, and only the buttons for it. The
  // thread below is history. (ตามของ waiting on the supervisor uses the assist
  // panel further down instead — that IS its decision.)
  const supTask = (() => {
    if (!isSupervisor || caseTicket.status === 'closed') return null;
    if (caseTicket.status === 'waiting_supervisor') return isTracking ? null : { kind: 'decide', title: '📌 ตัดสินใจเคสนี้' };
    if (caseTicket.status === 'in_progress' && caseTicket.turn === 'owner') {
      return { kind: 'result', title: isInternal ? '📌 ตอบกลับเคสนี้' : '📌 ผลลัพธ์เคสนี้' };
    }
    const waitingOn = { admin: 'แอดมิน', customer: 'ลูกค้า', hunting: 'ระบบตามหาของ', warehouse: 'Kluay เช็ค' }[caseTicket.turn];
    return {
      kind: 'wait', title: `⏳ รอ${waitingOn || 'ขั้นตอนถัดไป'} — หัวหน้าไม่ต้องทำอะไรตอนนี้`,
      // what was decided stays in view while the admin relays it
      draft: caseTicket.status === 'customer_update' ? latestDraft?.message : null,
    };
  })();
  const canDecide = !isInternal && !isTracking; // ตามของ decides in the assist panel

  // The admin's card shows the newest drafted message, and each new draft
  // restates every promise still outstanding — so an older draft that has not
  // been sent yet is stale, and copying it would under-tell the customer.
  const lastInformedAt = [...comments].reverse().find((c) => c.kind === 'informed')?.createdAt;
  const latestUpdateId = [...comments].reverse().find((c) => c.kind === 'customer_update')?.id;
  const isSupersededDraft = (c) => c.kind === 'customer_update' && c.id !== latestUpdateId
    && (!lastInformedAt || new Date(c.createdAt) > new Date(lastInformedAt));

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="text-2xl font-bold">เคส #{caseTicket.id}</h2>
        {claimedByOther && (
          <>
            <span className="bg-amber-100 text-amber-800 text-sm px-3 py-1 rounded-full font-medium">
              👤 กำลังดำเนินการโดย {caseTicket.claimedByName || 'แอดมินท่านอื่น'}
            </span>
            {!isSupervisor && (
              <button onClick={handleTakeover}
                className="text-sm text-amber-700 border border-amber-300 bg-amber-50 hover:bg-amber-100 px-3 py-1 rounded-full">
                รับช่วงต่อ
              </button>
            )}
          </>
        )}
        {caseTicket.claimedByMe && (
          <>
            <span className="bg-emerald-100 text-emerald-800 text-sm px-3 py-1 rounded-full font-medium">
              ✋ คุณรับเคสนี้อยู่
            </span>
            <button onClick={handleReleaseClaim}
              className="text-sm text-gray-500 underline hover:text-gray-700">
              คืนเคส
            </button>
          </>
        )}
        {caseTicket.status === 'waiting_customer' && (
          <span className="bg-yellow-100 text-yellow-800 text-sm px-3 py-1 rounded-full font-medium">
            ⏳ รอลูกค้าตอบ{(() => {
              const entry = (Array.isArray(caseTicket.statusHistory) ? caseTicket.statusHistory : [])
                .filter((h) => h.status === 'waiting_customer').pop();
              return entry ? ` ตั้งแต่ ${formatLogTime(entry.changedAt)}` : '';
            })()}
          </span>
        )}
      </div>

      <div className="mb-6">
        <CaseTicketStatusStepper status={caseTicket.status} type={caseTicket.type} issueType={caseTicket.issueType} />
      </div>

      {/* Supervisor's follow-up control on accepted cases: see the due mark,
          snooze +2 days if the outside party (e.g. seller) still hasn't replied. */}
      {isSupervisor && caseTicket.status === 'in_progress' && caseTicket.followUpAt && (
        <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-gray-600">
          <span>⏰ ครบกำหนดติดตาม: <b>{formatLogTime(caseTicket.followUpAt)}</b></span>
          <button onClick={handleExtendFollowUp}
            className="text-amber-700 border border-amber-300 bg-amber-50 hover:bg-amber-100 px-3 py-1 rounded-lg">
            ต่อเวลาอีก 2 วัน
          </button>
        </div>
      )}

      {adminTask && (
        <div className={`border-2 rounded-lg p-4 mb-4 ${
          ['note', 'info'].includes(adminTask.kind) ? 'border-gray-200 bg-gray-50'
            : adminTask.waiting ? 'border-yellow-400 bg-yellow-50'
              : 'border-indigo-400 bg-indigo-50'
        }`}>
          <h3 className={`font-bold mb-2 ${['note', 'info'].includes(adminTask.kind) ? 'text-gray-500 text-sm' : 'text-indigo-900'}`}>
            {adminTask.title}
          </h3>

          {adminTask.instruction && (
            <div className="bg-white border border-indigo-100 rounded-lg p-3 mb-3">
              <p className="text-gray-800 whitespace-pre-wrap text-sm">{adminTask.from || '👤 หัวหน้า'}: {adminTask.instruction}</p>
              {/* Only a draft (customer_update) is written to the customer, to be sent
                  verbatim. A supervisor's instruction is for the admin — no copy, or
                  "แจ้งลูกค้าว่า…" ends up pasted to the customer. */}
              {['informTracking', 'informDecision', 'confirm'].includes(adminTask.kind) && (
                <button
                  onClick={() => navigator.clipboard.writeText(adminTask.instruction)
                    .then(() => toast.success('คัดลอกข้อความแล้ว — วางในแชทลูกค้าได้เลย'))
                    .catch(() => toast.error('คัดลอกไม่สำเร็จ'))}
                  className="mt-2 text-xs border border-indigo-300 text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded">
                  📋 คัดลอกข้อความ
                </button>
              )}
            </div>
          )}

          {adminTask.kind === 'held' && (
            <p className="text-sm text-amber-700">
              เคสนี้ {caseTicket.claimedByName || 'แอดมินท่านอื่น'} ดูแลอยู่ — ถ้าจำเป็นต้องทำแทน กดปุ่ม "รับช่วงต่อ" ด้านบน
            </p>
          )}

          {adminTask.kind === 'answer' && (
            <>
              {adminTask.waiting && (
                <p className="text-sm text-yellow-800 mb-2">ได้คำตอบจากลูกค้าแล้ว พิมพ์ส่งตรงนี้ได้เลย — ระบบจะส่งต่อให้หัวหน้าเอง</p>
              )}
              <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
                <TextField fullWidth size="small" multiline minRows={1} placeholder="พิมพ์คำตอบ..."
                  value={taskReply} onChange={(e) => setTaskReply(e.target.value)} />
                <button onClick={() => handleAddComment('answer', taskReply)} disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium whitespace-nowrap">
                  ส่งคำตอบ ✓
                </button>
              </div>
              {!adminTask.waiting && (
                <div className="mt-2 text-sm text-gray-500 flex items-center gap-2">
                  ยังไม่ได้คำตอบ? →
                  <button onClick={handleWaitCustomer}
                    className="text-yellow-700 border border-yellow-300 bg-yellow-50 hover:bg-yellow-100 px-3 py-1 rounded-lg">
                    ⏳ ถามลูกค้าแล้ว รอตอบ
                  </button>
                </div>
              )}
            </>
          )}

          {adminTask.kind === 'inform' && (
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
              บอกลูกค้าตามนี้ แล้วกด →
              <button onClick={() => handleInformedCustomer()} disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
                ✓ แจ้งลูกค้าแล้ว
              </button>
            </div>
          )}

          {adminTask.kind === 'informDecision' && (
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
              {parseFloat(caseTicket.proposedRefundAmount) > 0 && (
                <span>ยอดที่เสนอ: <b>{caseTicket.proposedRefundAmount}</b> บาท</span>
              )}
              แจ้งผลนี้ให้ลูกค้า แล้วกด →
              <button onClick={() => handleInformedCustomer('ยืนยันว่าแจ้งผลให้ลูกค้าแล้ว?', '📢 แจ้งผลลูกค้าแล้ว รอลูกค้าตอบ')} disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
                ✓ แจ้งลูกค้าแล้ว
              </button>
            </div>
          )}

          {adminTask.kind === 'informTracking' && (
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
              {untoldFound.length > 0 && (
                <span className="w-full text-green-800">
                  ✅ เจอของแล้ว {untoldFound.length} รายการ — ชิ้นที่เจอไม่มีการคืนเงิน
                  {!stillHunting && !items.some((it) => it.foundStatus === 'not_found') && ' · กดแล้วระบบปิดเคสให้'}
                </span>
              )}
              {items.some((it) => it.foundStatus === 'not_found') && (
                <span className="w-full text-purple-800">
                  {isAppCustomer
                    ? '💸 ลูกค้าใช้แอป — กดแล้วระบบจะปิดเคส ยกเลิกออเดอร์ และคืนเงินเข้ากระเป๋าลูกค้าทันที'
                    : '💸 กดแล้วระบบจะสร้างรายการคืนเงิน (หรือยกเลิกออเดอร์ที่ยังไม่ได้ชำระ) ตามยอดในข้อความนี้'}
                </span>
              )}
              ส่งข้อความนี้ให้ลูกค้า แล้วกด →
              <button onClick={handleInformedTracking} disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
                ✓ แจ้งลูกค้าแล้ว
              </button>
            </div>
          )}

          {adminTask.kind === 'ack' && (
            <button onClick={() => handleInformedCustomer('รับทราบข้อความของหัวหน้าแล้ว?', '✓ รับทราบแล้ว')} disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
              ✓ รับทราบแล้ว
            </button>
          )}

          {adminTask.kind === 'confirm' && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
                {parseFloat(caseTicket.proposedRefundAmount) > 0 && (
                  <span>ยอดที่เสนอ: <b>{caseTicket.proposedRefundAmount}</b> บาท</span>
                )}
                ลูกค้าตกลง →
                <button onClick={handleConfirmCustomer} disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
                  {parseFloat(caseTicket.proposedRefundAmount) > 0
                    ? '✓ ลูกค้าตกลง → ไปโอนเงินคืน'
                    : '✓ ลูกค้าตกลง — จบเคส'}
                </button>
              </div>
              <div className="mt-3 pt-3 border-t border-indigo-100">
                <p className="text-sm text-gray-500 mb-1">ลูกค้าไม่ตกลง / ต่อรอง? พิมพ์คำตอบของลูกค้าส่งกลับหัวหน้า:</p>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
                  <TextField fullWidth size="small" multiline minRows={1} placeholder='เช่น "ลูกค้าไม่โอเค ขอ 500 บาท"'
                    value={taskReply} onChange={(e) => setTaskReply(e.target.value)} />
                  <button onClick={() => handleAddComment('counter', taskReply)} disabled={saving || !taskReply.trim()}
                    className="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white px-4 py-2 rounded-lg font-medium whitespace-nowrap">
                    ส่งกลับหัวหน้า
                  </button>
                </div>
              </div>
            </>
          )}

          {adminTask.kind === 'refund' && (
            <button onClick={() => handleGoToRefund()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium">
              ไปหน้าโอนเงินคืน →
            </button>
          )}
        </div>
      )}

      {supTask && (
        <div className={`border-2 rounded-lg p-4 mb-4 ${supTask.kind === 'wait' ? 'border-gray-200 bg-gray-50' : 'border-blue-400 bg-blue-50'}`}>
          <h3 className={`font-bold mb-2 ${supTask.kind === 'wait' ? 'text-gray-500 text-sm' : 'text-blue-900'}`}>{supTask.title}</h3>
          {supTask.draft && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">ข้อความที่ให้แอดมินส่งลูกค้า:</p>
              <p className="text-gray-800 whitespace-pre-wrap text-sm">{supTask.draft}</p>
            </div>
          )}

          {supTask.kind !== 'wait' && !showDecision && (
            <>
              <TextField fullWidth size="small" multiline minRows={2} placeholder="พิมพ์ข้อความ แล้วเลือกว่าจะให้ทำอะไร..."
                value={newComment} onChange={(e) => setNewComment(e.target.value)} sx={{ bgcolor: 'white', mb: 1 }} />
              <div className="flex flex-wrap gap-2">
                {supTask.kind === 'decide' ? (
                  <>
                    <button onClick={() => handleAddComment('request_info')} disabled={saving}
                      className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
                      ขอข้อมูลเพิ่ม
                    </button>
                    <button onClick={() => handleAddComment('accept')} disabled={saving}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
                      รับเรื่อง (ให้แอดมินแจ้งลูกค้าว่ากำลังดำเนินการ)
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleAddComment('relay')} disabled={saving}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
                      {isInternal ? 'ตอบกลับแอดมิน' : 'ให้แอดมินแจ้งลูกค้า'}
                    </button>
                    <button onClick={() => handleAddComment('task')} disabled={saving}
                      className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
                      ให้แอดมินทำ/ตอบกลับ
                    </button>
                  </>
                )}
                {canDecide && (
                  <button onClick={() => setShowDecision(true)} disabled={saving}
                    className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
                    {supTask.kind === 'decide' ? 'ตัดสินใจเลย' : 'ตัดสินใจ'} (ข้อความถึงลูกค้า + ยอดเงิน)
                  </button>
                )}
              </div>
            </>
          )}

          {supTask.kind !== 'wait' && showDecision && canDecide && (
            <>
              <RefundCalculator
                order={caseTicket.order}
                onApply={(amount, breakdown) => {
                  setProposedRefundAmount(String(amount));
                  setCustomerUpdateMessage((prev) => (prev ? `${prev}\n${breakdown}` : breakdown));
                }}
              />
              <TextField fullWidth multiline minRows={2} label="ข้อความถึงลูกค้า (แอดมินจะก๊อปไปส่ง)"
                value={customerUpdateMessage} onChange={(e) => setCustomerUpdateMessage(e.target.value)}
                sx={{ mb: 2, bgcolor: 'white' }} />
              <TextField label="ยอดเงินที่เสนอ (ถ้ามี)" type="text" inputMode="decimal" sx={{ bgcolor: 'white' }}
                value={proposedRefundAmount}
                onChange={(e) => setProposedRefundAmount(e.target.value.replace(/[^\d.]/g, ''))} />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button onClick={handleCustomerUpdate} disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium">
                  บันทึก — ให้แอดมินแจ้งลูกค้า
                </button>
                <button onClick={() => setShowDecision(false)} className="text-sm text-gray-500 underline">← ยกเลิก</button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="font-bold text-lg">{ticketDisplayName(caseTicket)}</span>
          {caseTicket.customer?.shop && <ShopBadge shop={caseTicket.customer.shop} />}
          <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full font-medium">
            {ISSUE_TYPE_LABELS[caseTicket.issueType] || caseTicket.issueType}
          </span>
          {caseTicket.shippingChannel && (
            <span className="bg-sky-100 text-sky-700 text-xs px-2 py-0.5 rounded-full font-medium">
              🚚 {SHIPPING_CHANNEL_LABELS[caseTicket.shippingChannel] || caseTicket.shippingChannel}
            </span>
          )}
          {caseTicket.productGroup && (
            <span className="bg-teal-100 text-teal-700 text-xs px-2 py-0.5 rounded-full font-medium">
              📦 {caseTicket.productGroup}
            </span>
          )}
          {caseTicket.isAir !== null && caseTicket.isAir !== undefined && (
            <span className="bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full font-medium">
              {caseTicket.isAir ? '✈️ แอร์' : '🚢 เรือ'}
            </span>
          )}
          <span className="text-gray-400 text-sm ml-auto">
            แจ้งเมื่อ {formatDate(caseTicket.reportedDate || caseTicket.createdAt)}
          </span>
        </div>
        {caseTicket.order && (
          <p className="text-sm text-gray-500 mb-2">
            ออเดอร์: {caseTicket.order.smileNameInput} — {caseTicket.order.amount} บาท ({formatDate(caseTicket.order.orderDate)})
            {caseTicket.order.url && caseTicket.order.url !== '-' && (
              <a href={caseTicket.order.url} target="_blank" rel="noreferrer" className="text-blue-700 underline truncate max-w-md block">
                {caseTicket.order.url}
              </a>
            )}
          </p>
        )}
        {/* ฝากส่ง: the chased parcels, by tracking number (send-only orders have no link) */}
        {caseTicket.issueType === 'sendonly_follow' && items.length > 0 && (
          <div className="text-sm text-gray-600 mb-2">
            <div className="font-medium text-gray-700">📦 รายการฝากส่งที่ตาม ({items.length})</div>
            {items.map((it) => (
              <div key={it.id} className="flex flex-wrap gap-x-3">
                <span className="font-mono">{it.trackingJp || 'ไม่มีเลขพัสดุ'}</span>
                {it.order?.smileNameInput && <span>{it.order.smileNameInput}</span>}
                {it.orderDate && <span className="text-gray-400">ส่ง {formatDate(it.orderDate)}</span>}
              </div>
            ))}
          </div>
        )}
        <p className="text-gray-800 whitespace-pre-wrap">{caseTicket.description}</p>

        {caseTicket.hasNoAttachment && attachments.length === 0 && (
          <p className="text-sm text-gray-400 mt-2">ลูกค้าไม่มีรูป/วิดีโอแนบ</p>
        )}

        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-3">
            {attachments.map((a, i) => (
              a.type === 'video'
                ? (
                  <div key={i} className="flex flex-col">
                    <video src={a.url} controls className="h-40 rounded border border-gray-200" />
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 mt-1">
                      🔍 เปิดวิดีโอเต็มจอ
                    </a>
                  </div>
                )
                : <img key={i} src={a.url} alt="" onClick={() => setEnlargedImage(a.url)}
                    className="h-40 object-contain rounded border border-gray-200 cursor-zoom-in hover:opacity-80" />
            ))}
          </div>
        )}

        <div className="mt-3">
          <label className="text-sm text-blue-600 cursor-pointer">
            + แนบรูป/วิดีโอเพิ่ม
            <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={handleExtraFileChange} />
          </label>
        </div>
      </div>

      {/* Both can be true at once: Japan is still looking at some parcels while
          the supervisor already owes a decision on the ones they gave up on.
          The panel stays while anything is hunted — a parcel Japan gave up on
          can still turn up. */}
      {isTracking && caseTicket.status !== 'closed'
        && items.some((it) => it.foundStatus === 'pending') && (
        <WarehousePanel caseTicket={caseTicket} items={items} onDone={load} />
      )}

      {/* ตกหล่น finds wait for the supervisor's compensation, whatever else the case is doing */}
      {isTracking && isSupervisor && caseTicket.status !== 'closed' && (
        <SupervisorLapsePanel caseTicket={caseTicket} items={items} onDone={load} />
      )}

      {isTracking && isSupervisor && caseTicket.status === 'waiting_supervisor' && (
        <SupervisorAssistPanel caseTicket={caseTicket} items={items} onDone={load} />
      )}

      {/* One line saying what this case is waiting for. The admin's own card
          below carries the button; this is so anyone looking — the supervisor
          especially, who has no card — can see the state without decoding it. */}
      {isTracking && caseTicket.status !== 'closed' && (() => {
        const next = caseTicket.status === 'waiting_warehouse'
          ? '📦 รอผลจาก Kluay — ใครก็ได้บันทึกผลที่กล่องสีส้มด้านบน'
          : caseTicket.status === 'waiting_supervisor'
            ? '💰 รอหัวหน้าเลือกส่วนลดค่าน้ำหนัก ที่กล่องสีน้ำเงินด้านบน'
            : caseTicket.status === 'customer_update'
              ? '📢 รอแอดมินก๊อปข้อความ "แจ้งลูกค้า" ด้านบนไปส่งให้ลูกค้า แล้วกด "แจ้งลูกค้าแล้ว"'
              : caseTicket.status === 'customer_informed'
                ? HUNTING_NOTE
                : '⏳ ระบบเช็คให้ทุกชั่วโมงว่าของเข้ารอบหรือยัง';
        return (
          <div className="border border-indigo-200 bg-indigo-50 rounded-lg px-4 py-2 mb-4 text-sm text-indigo-900">
            <b>ตอนนี้ต้องทำอะไร:</b> {next}
            {caseTicket.fullRefundDueAt && (() => {
              // ส่งผิดขนส่ง: the customer got "ประมาณ 3-7 วัน", no date — the deadline is ours.
              const hunting = items.filter((it) => it.foundStatus === 'pending');
              const onlyMisrouted = hunting.length > 0 && hunting.every((it) => it.misroutedAt);
              return onlyMisrouted
                ? <span className="block mt-1">⏳ กำหนดภายใน: ถ้าถึง <b>{formatDate(caseTicket.fullRefundDueAt)}</b> ยังไม่ได้รับ คืนเงินส่วนที่ชำระแล้ว (ไม่ได้แจ้งวันที่นี้กับลูกค้า)</span>
                : <span className="block mt-1">🤝 สัญญากับลูกค้า: ถ้าถึง <b>{formatDate(caseTicket.fullRefundDueAt)}</b> ยังไม่พบ คืนเงินส่วนที่ชำระแล้ว (ถ้ายังไม่ได้ชำระ = ยกเลิกรายการ)</span>;
            })()}
          </div>
        );
      })()}

      {/* ตามของ — the parcels being chased, each with where the system thinks it
          is (live), plus the two things that can happen while the warehouse
          looks: the customer chases again, or names one more parcel. */}
      {isTracking && (
        <div className="border border-orange-200 rounded-lg p-4 mb-4 bg-orange-50">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="font-semibold text-orange-900">📦 รายการที่ตามหา ({items.length})</h3>
            {/* A parcel we already refunded / cancelled turned up — money or goods
                to sort out with the customer. Stays visible after the case closes. */}
            {caseTicket.foundAfterRefundAt && (
              <div className="w-full border border-amber-400 bg-amber-100 text-amber-900 rounded px-3 py-2 text-sm">
                📦 <b>ของที่คืนเงิน/ยกเลิกไปแล้ว กลับเข้าระบบ</b> ({formatDate(caseTicket.foundAfterRefundAt)}) — ตรวจสอบและคุยกับลูกค้า
              </div>
            )}
            {caseTicket.status !== 'closed' && (
              <div className="flex items-center gap-1">
                <TextField size="small" placeholder="ลูกค้าทวงอีก — โน้ตสั้นๆ (ไม่บังคับ)" value={chaseNote}
                  onChange={(e) => setChaseNote(e.target.value)} sx={{ bgcolor: 'white', minWidth: 260 }} />
                <button onClick={handleAddChase} disabled={saving}
                  className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap">
                  📣 ลูกค้าทวงอีกครั้ง
                </button>
              </div>
            )}
          </div>

          <table className="w-full text-sm bg-white rounded border border-orange-100">
            <thead className="bg-orange-100 text-orange-900 text-xs">
              <tr>
                <th className="p-2 text-left">สินค้า</th>
                <th className="p-2 text-left">เลขพัสดุ</th>
                <th className="p-2 text-right">ราคา (เยน)</th>
                <th className="p-2 text-left min-w-[8rem]">ระบบเจอที่ไหน</th>
                <th className="p-2 text-left min-w-[10rem]">สถานะ</th>
                <th className="p-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-orange-50 align-top">
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      {it.order?.imageUrl && <img src={it.order.imageUrl} alt="" className="w-8 h-8 object-cover rounded" />}
                      <div className="min-w-0">
                        <a href={it.url} target="_blank" rel="noreferrer" className="text-blue-700 underline text-sm truncate max-w-xs block">
                          {it.url}
                        </a>
                        <div className="text-xs text-gray-400">
                          {it.order?.website ? `${it.order.website} · ` : ''}
                          {it.orderDate ? `สั่ง ${formatDate(it.orderDate)}` : ''}
                        </div>
                        {/* the weight-fee decision: where it stands on the order when
                            there is a discount; "ไม่ลดค่าน้ำหนัก" so a decision made is visible */}
                        {it.weightAssist
                          ? <WeightAssistTag status={it.weightAssist} hideCase />
                          : it.assistDecidedAt && (
                            <div className={`text-xs ${Number(it.assistPercent) > 0 ? 'text-blue-700' : 'text-gray-400'}`}>
                              {Number(it.assistPercent) >= 100 ? 'ฟรีค่าน้ำหนัก'
                                : Number(it.assistPercent) > 0 ? `ลดค่าน้ำหนัก ${Number(it.assistPercent)}%` : 'ไม่ลดค่าน้ำหนัก'}
                            </div>
                          )}
                      </div>
                    </div>
                  </td>
                  <td className="p-2 font-mono text-xs">{it.trackingJp || <span className="text-gray-300">—</span>}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    {it.priceYen != null ? Number(it.priceYen).toLocaleString() : '—'}
                  </td>
                  <td className="p-2"><DiagnosisChip diagnosis={it.diagnosis} /></td>
                  <td className="p-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                      it.foundStatus === 'found' ? 'bg-green-100 text-green-800'
                        : it.foundStatus === 'not_found' ? 'bg-red-100 text-red-800'
                          : it.foundStatus === 'refunded' ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-700'
                    }`}>
                      {FOUND_STATUS_LABELS[it.foundStatus] || it.foundStatus}
                    </span>
                    {it.misroutedAt && it.foundStatus === 'pending' && (
                      <div className="text-xs text-amber-700 mt-1 whitespace-nowrap">📦 ส่งผิดขนส่ง — รอนำกลับ</div>
                    )}
                    {it.foundStatus === 'found' && !it.foundToldAt && (
                      <div className="text-xs text-green-700 mt-1">📢 รอแจ้งลูกค้า</div>
                    )}
                    {/* Past due: what the customer was quoted (the refund-due message) */}
                    {it.foundStatus === 'not_found' && it.refundAmount != null && (
                      <div className="text-xs text-red-700 mt-1">
                        {Number(it.refundAmount) > 0
                          ? `${isAppCustomer ? 'จะคืนเข้ากระเป๋า' : 'จะคืน'} ฿${baht(it.refundAmount)}`
                          : 'จะยกเลิก (ยังไม่ได้ชำระ)'}
                      </div>
                    )}
                    {/* Settled: a refund waiting on the refund page, or an order closed with nothing to return */}
                    {it.foundStatus === 'refunded' && (
                      <div className="text-xs text-purple-700 mt-1">
                        {it.refundId ? (
                          <button onClick={() => navigate('/refund')} className="underline hover:text-purple-900">
                            รายการคืนเงิน #{it.refundId}{Number(it.refundAmount) > 0 ? ` · ฿${baht(it.refundAmount)}` : ''} →
                          </button>
                        ) : Number(it.refundAmount) > 0 && it.order?.productPayment === 'คืนเงินแล้ว' ? (
                          // App customer: the click credited the wallet
                          `คืนเข้ากระเป๋าแล้ว ฿${baht(it.refundAmount)}`
                        ) : Number(it.refundAmount) > 0 ? (
                          // App customer closed before the click credited wallets — opens the refund modal on this parcel
                          <button onClick={() => goToWalletRefund([it])} className="underline hover:text-purple-900">
                            คืนเข้ากระเป๋า ฿{baht(it.refundAmount)} — ทำที่หน้าโอนเงินคืน →
                          </button>
                        ) : 'ยกเลิกแล้ว (ยังไม่ได้ชำระ)'}
                      </div>
                    )}
                    {/* The round it travels on: known when found, expected when not */}
                    {(it.foundShipDate || it.expectedShipDate) && (
                      <div className="text-xs text-gray-500 mt-1 whitespace-nowrap">
                        {it.foundShipDate
                          ? <>{it.foundShipDate.isAir ? '✈️' : '🚢'} {formatDate(it.foundShipDate.shipDate)}</>
                          : <>{it.misroutedAt ? 'ส่งออก' : 'คาดว่า'} {it.expectedShipDate.isAir ? '✈️' : '🚢'} {formatDate(it.expectedShipDate.shipDate)}</>}
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {caseTicket.status !== 'closed' && it.foundStatus === 'pending' && !it.assistAppliedAt && items.length > 1 && (
                      <button onClick={() => handleRemoveItem(it)} className="text-gray-400 hover:text-red-600" title="ลบรายการ">✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {caseTicket.status !== 'closed' && (
            <div className="mt-3">
              <OrderPicker
                customerId={caseTicket.customerId}
                pickedIds={items.map((it) => it.orderId)}
                onPick={handleAddItem}
                label="ลูกค้าตามเพิ่ม — ค้นหาออเดอร์ (ชื่อ / ลิงก์ / เลขพัสดุ)"
              />
            </div>
          )}
        </div>
      )}

      {/* The case's history, in order: every action's message, Kluay's answers,
          chases, drafts to the customer. Read-only for everyone — actions live in
          each role's card at the top. */}
      <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700">
            ประวัติเคส
            <span className="text-xs font-normal text-gray-400"> (ดูอย่างเดียว — ทำงานผ่านกล่องด้านบน)</span>
          </h3>
        </div>

        {comments.length === 0 ? (
          <p className="text-gray-400 text-sm mb-3">ยังไม่มีข้อความ</p>
        ) : (
          <div className="flex flex-col gap-2 mb-3">
            {comments.map((c) => {
              const isSupervisorMsg = c.author?.role === 'superuser';
              const isCustomerUpdate = c.kind === 'customer_update';
              const isChase = c.kind === 'chase';
              const isWarehouse = c.kind === 'warehouse';
              // No author = written by the hourly ตามของ sweep, not a person.
              const isSystem = !c.author && !c.authorId;
              return (
                <div key={c.id}
                  className={`rounded-lg p-3 max-w-lg ${
                    isCustomerUpdate
                      ? 'bg-purple-50 border border-purple-200' // decision = owner's message → owner side (left)
                      : isSystem
                        ? 'bg-gray-50 border border-gray-200'
                      : isWarehouse
                        ? 'bg-amber-50 border border-amber-200' // what Japan said — neither side's words
                        : isChase
                          ? 'bg-orange-50 border border-orange-200 ml-auto' // the customer's voice, logged by the admin
                          : isSupervisorMsg
                            ? 'bg-blue-50 border border-blue-100'
                            : 'bg-emerald-50 border border-emerald-100 ml-auto'
                  }`}>
                  <p className="text-xs font-semibold text-gray-500 mb-1">
                    {isCustomerUpdate ? (isSystem ? '📢 แจ้งลูกค้า (🔔 ระบบร่าง)' : '📢 แจ้งลูกค้า') : isSystem ? '🔔 ระบบ' : isWarehouse ? '🏭 Kluay ตอบ' : isChase ? '📣 ลูกค้าทวง' : isSupervisorMsg ? '👤 หัวหน้า' : '👤 แอดมิน'}
                    {c.author?.nickname ? ` — ${c.author.nickname}` : ''} · {formatDate(c.createdAt)}
                    {isSupersededDraft(c) && <span className="ml-1 font-normal text-gray-400">— มีข้อความใหม่กว่าแทนแล้ว อย่าส่งอันนี้</span>}
                  </p>
                  <p className={`whitespace-pre-wrap text-sm ${isSupersededDraft(c) ? 'text-gray-400 line-through decoration-gray-300' : 'text-gray-800'}`}>{c.message}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Linked refund — the supervisor's decision and the customer's agreement
          now live in the cards at the top; only the outcome shows here. */}
      {!isInternal && !isTracking && caseTicket.refundId && (
        <p className="text-gray-500 text-sm mb-4">เชื่อมรายการโอนคืน #{caseTicket.refundId} แล้ว</p>
      )}

      {/* Manual close & reopen are SUPERVISOR-ONLY (backend-enforced too):
          closing without a refund is a judgement call — admins close cases
          only through the refund path, which closes automatically. */}
      {caseTicket.status !== 'closed' && isSupervisor && (
        <div className="flex justify-end mb-4">
          <button onClick={handleClose}
            className="text-xs text-gray-400 hover:text-gray-600 underline">
            {isInternal ? 'ปิดเคส (รับทราบแล้ว)' : 'ปิดเคส (ไม่ต้องคืนเงิน)'}
          </button>
        </div>
      )}

      {/* Undo an accidental close — jump back to whichever step it should still be at */}
      {caseTicket.status === 'closed' && isSupervisor && (
        <div className="border border-amber-200 rounded-lg p-4 bg-amber-50 flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-amber-800">เผื่อกดปิดเคสผิด:</span>
          <select value={reopenStatus} onChange={(e) => setReopenStatus(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white">
            <option value="">เลือกสถานะที่จะย้อนกลับไป</option>
            <option value="waiting_supervisor">รอหัวหน้าตอบ</option>
            <option value="in_progress">อยู่ระหว่างดำเนินการ</option>
            {!isInternal && <option value="customer_update">รอแอดมินแจ้งลูกค้า</option>}
            {!isInternal && <option value="customer_informed">แจ้งลูกค้าแล้ว</option>}
            {!isInternal && <option value="customer_confirmed">ลูกค้ายืนยันแล้ว</option>}
          </select>
          <button onClick={handleReopen} disabled={!reopenStatus || saving}
            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-4 py-1.5 rounded text-sm font-medium">
            เปิดเคสกลับ
          </button>
        </div>
      )}

      {/* Audit log — every status transition with who/when, straight from
          statusHistory (names resolved by the backend). Comments carry their
          own author labels in the thread above. */}
      {Array.isArray(caseTicket.statusHistory) && caseTicket.statusHistory.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4 mt-4 bg-gray-50">
          <h3 className="font-semibold text-gray-600 text-sm mb-2">📜 ประวัติการดำเนินการ</h3>
          <div className="flex flex-col gap-1">
            {(() => {
              const hs = caseTicket.statusHistory;
              // the status a row moved FROM (extend_followup is a note, not a move)
              const fromOf = (i) => hs.slice(0, i).reverse().find((x) => x.status !== 'extend_followup')?.status;
              const lastMove = hs.reduce((at, x, i) => (x.status !== 'extend_followup' ? i : at), -1);
              return hs.map((h, i) => {
                // "เปิดเคส" and its hand-off are one click — show them as one row
                const next = hs[i + 1];
                if (h.status === 'open' && next && next.changedAt === h.changedAt && next.changedBy === h.changedBy) return null;
                const opened = fromOf(i) === 'open' && hs[i - 1]?.changedAt === h.changedAt;
                const nextUp = h.status === 'extend_followup' ? null : historyNext(h.status, isTracking);
                const isNow = i === lastMove;
                return (
                  <div key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm border-b border-gray-100 py-1.5 last:border-0">
                    <span className="text-gray-400 text-xs w-28 shrink-0">{formatLogTime(h.changedAt)}</span>
                    <span className="text-gray-500 text-xs">{historyActor(h)}</span>
                    <span className="font-medium text-gray-800">{opened ? 'เปิดเคส · ' : ''}{historyAction(h, fromOf(i))}</span>
                    {nextUp && (
                      <span className={`ml-auto text-xs rounded-full px-2 py-0.5 ${isNow ? 'bg-blue-600 text-white font-semibold' : 'bg-gray-200 text-gray-600'}`}>
                        {isNow ? 'ตอนนี้: ' : 'ต่อไป: '}{nextUp}
                      </span>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {enlargedImage && <ImageModal src={enlargedImage} onClose={() => setEnlargedImage(null)} />}
    </div>
  );
};

export default CaseTicketDetail;
